import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Inject,
  input,
  Input,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { isPlatformServer, NgForOf, NgIf } from '@angular/common';

import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { Skus } from '@helper/skus';
import { ProductModel } from '../../product/product.model';
import { Product_SkuColor } from '@proto/store/api/store/product';
import { SizeInfo } from '@helper/size-info';
import { MatDialog } from '@angular/material/dialog';
import { FavoriteModel } from '../../favorite/favorite.model';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Price } from '@common/pipes';
import { StepperComponent } from '@common/stepper/stepper.component';
import { ProductGalleryComponent } from '@common/product-header/product-gallery/product-gallery.component';
import { InfoType, ProductInfoComponent } from '@common/product-header/product-info/product-info.component';
import { Links } from '@helper/links';
import { RouteHelper } from '@helper/route-paths';
import { ShareComponent } from '@common/share/desktop/share.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-product-header',
  templateUrl: './product-header.component.html',
  styleUrls: ['./product-header.component.scss'],
  imports: [
    MatSnackBarModule,
    ImageLoadingComponent,
    FocusRemoverDirective,
    MatSnackBarModule,
    NgIf,
    RouterLink,
    Price,
    StepperComponent,
    NgForOf,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductHeaderComponent {
  readonly galleryHeight = 1000;
  readonly windowBuffer = 100;
  readonly infoType = InfoType;
  readonly isFavorite = signal(false);
  readonly shareProductId = input<string | undefined>(undefined);
  readonly shareProductUrl = computed(() =>
    this.shareProductId() ? `${environment.endPointUrl}/${RouteHelper.product(this.shareProductId() ?? '')}` : undefined
  );

  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) viewModel!: ProductModel;
  @Input() skuId?: string;
  @Input() showDetailLink = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    public toast: MatSnackBar,
    private dialog: MatDialog,
    private favoriteModel: FavoriteModel
  ) {
    effect(() => {
      if (isPlatformServer(this.platformId)) {
        return;
      }
      this.isFavorite.set(this.favoriteModel.favorites().some(i => i === Skus.fromId(this.skuId ?? '').productId));
    });
  }

  openGallery(): void {
    const bigImages = this.viewModel
      .apiResult()
      .colors.map(c => Skus.getImage1500(c, this.viewModel.currSize()?.size))
      .filter(i => !!i);
    if (!bigImages.length) {
      return;
    }
    ProductGalleryComponent.open(
      this.dialog,
      Math.max(bigImages.indexOf(Skus.getImage1500(this.viewModel.currColor(), this.viewModel.currSize()?.size)), 0),
      bigImages,
      Math.min(this.galleryHeight, window.innerHeight - this.windowBuffer)
    );
  }

  openProductInfo(type: InfoType): void {
    ProductInfoComponent.open(this.dialog, type, this.viewModel.apiResult());
  }

  openProduct(): void {
    Links.openInNewWindow(
      RouteHelper.product(
        Skus.skuId(
          this.viewModel.apiResult().meta?.productId || '',
          this.viewModel.currColor()?.id || '',
          this.viewModel.currSize()?.size.id || ''
        )
      )
    );
  }

  toggleFavorite(productId?: string): void {
    if (productId) {
      this.favoriteModel.toggleFavorite(this.destroyRef, productId, this.toast, this.viewModel.apiResult());
    }
  }

  toggleShare(): void {
    ShareComponent.open(this.dialog, this.shareProductUrl());
  }

  trackByColor(_: number, color: Product_SkuColor): string {
    return color.id;
  }

  trackBySize(_: number, value: SizeInfo): string {
    return value.size.id;
  }
}
